/*
  fd-pdp-jump-nav — sticky in-page tabs with scroll spy.

  Links hash to [id] hooks rendered by snippets/fd-jump-anchor.liquid.
  The observed target is the Shopify section wrapping that hook so the
  whole module counts as "in view", not a zero-height div.

  Sticky offset uses --header-height from snippets/header-height-sync.js.liquid.
  --fd-jn-height is published on :root so target sections can scroll-margin.
*/
class FdPdpJumpNav extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('[data-fd-jn-link]'));
    this.list = this.querySelector('[data-fd-jn-list]');
    this._lock = false;
    this._raf = 0;

    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onSectionLoad = this._onSectionLoad.bind(this);

    this.syncNavHeight();
    this.bindTargets();
    this.syncStuck();
    this.links.forEach((link) => link.addEventListener('click', this._onClick));
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
    document.addEventListener('shopify:section:load', this._onSectionLoad);

    this.sync();
    this._activateFromHash();
  }

  disconnectedCallback() {
    this.links.forEach((link) => link.removeEventListener('click', this._onClick));
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  _onSectionLoad() {
    this.bindTargets();
    this.syncNavHeight();
    this.syncStuck();
    this.sync();
  }

  bindTargets() {
    this.targets = this.links
      .map((link) => {
        const id = (link.getAttribute('href') || '').replace(/^#/, '');
        if (!id) return null;
        const hook = document.getElementById(id);
        if (!hook) return null;
        const el = hook.closest('.shopify-section') || hook;
        return { link, el };
      })
      .filter(Boolean);
  }

  headerHidden() {
    return Boolean(document.querySelector('.mobile-navbar.header-scroll--hidden'));
  }

  headerHeight() {
    if (this.headerHidden()) return 0;
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    const parsed = parseFloat(raw);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    const header = document.querySelector('.header-wrapper-container');
    return header ? Math.round(header.getBoundingClientRect().height) : 0;
  }

  stickyTop() {
    const height = this.headerHeight();
    return height > 0 ? height - 1 : 0;
  }

  syncNavHeight() {
    const height = Math.round(this.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--fd-jn-height', height + 'px');
  }

  syncStuck() {
    const section = this.closest('.fd-pdp-jump-nav-section') || this;
    const stickyTop = this.stickyTop();
    const stuck = Math.round(section.getBoundingClientRect().top) <= stickyTop;
    this.classList.toggle('is-stuck', stuck);
  }

  offsetLine() {
    return this.stickyTop() + this.getBoundingClientRect().height + 2;
  }

  _onResize() {
    this.syncNavHeight();
    this.syncStuck();
    this.sync();
  }

  _onScroll() {
    if (this._lock) return;
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      this.syncStuck();
      this.sync();
    });
  }

  _onClick(event) {
    const link = event.currentTarget;
    const id = (link.getAttribute('href') || '').replace(/^#/, '');
    const hook = id ? document.getElementById(id) : null;
    if (!hook) return;

    event.preventDefault();
    this._lock = true;
    this.setActive(link);

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    hook.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    if (history.replaceState) history.replaceState(null, '', '#' + id);

    let released = false;
    const unlock = () => {
      if (released) return;
      released = true;
      this._lock = false;
      this.sync();
    };

    if (reduce) {
      window.setTimeout(unlock, 50);
      return;
    }

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', unlock, { once: true });
      window.setTimeout(unlock, 1200);
    } else {
      window.setTimeout(unlock, 800);
    }
  }

  _activateFromHash() {
    const id = (window.location.hash || '').replace(/^#/, '');
    if (!id) return;
    const match = this.targets.find((t) => t.link.getAttribute('href') === '#' + id);
    if (match) this.setActive(match.link);
  }

  sync() {
    if (!this.targets.length) return;
    const line = this.offsetLine();
    let current = this.targets[0];
    for (let i = 0; i < this.targets.length; i += 1) {
      const top = this.targets[i].el.getBoundingClientRect().top;
      if (top <= line) current = this.targets[i];
    }
    this.setActive(current.link);
  }

  setActive(link) {
    if (link && link.classList.contains('is-active')) return;
    this.links.forEach((item) => {
      const on = item === link;
      item.classList.toggle('is-active', on);
      if (on) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
    if (link && this.list && link.scrollIntoView) {
      const left = link.offsetLeft;
      const right = left + link.offsetWidth;
      const viewLeft = this.list.scrollLeft;
      const viewRight = viewLeft + this.list.clientWidth;
      if (left < viewLeft || right > viewRight) {
        link.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }
    }
  }
}

if (!customElements.get('fd-pdp-jump-nav')) {
  customElements.define('fd-pdp-jump-nav', FdPdpJumpNav);
}
